import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { Offcanvas } from "bootstrap";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../../../assets/images/logo.png";
import "./Navbar.css";

const Navbar = () => {
  const [navbar, setNavbar] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // helper for pill nav links (top-level items)
  const mainNavLinkClass = ({ isActive }) =>
    `nav-link px-3 py-2 rounded-pill main-pill-link ${
      isActive ? "active-pill" : ""
    }`;

  // global scroll-to-top helper (used on all clicks)
  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    const pageContainer = document.getElementById("page-content");
    if (pageContainer) {
      pageContainer.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  // helper: close offcanvas (used from multiple places)
  const closeOffcanvas = () => {
    const offcanvasElement = document.getElementById("offcanvasNavbar");
    if (!offcanvasElement) return;
    const instance = Offcanvas.getInstance(offcanvasElement);
    if (instance) instance.hide();
  };

  // Change navbar background on scroll
  useEffect(() => {
    const changeBackground = () => setNavbar(window.scrollY >= 80);
    window.addEventListener("scroll", changeBackground);
    return () => window.removeEventListener("scroll", changeBackground);
  }, []);

  // Auto-close offcanvas when clicking any nav or submenu link
  useEffect(() => {
    const offcanvasElement = document.getElementById("offcanvasNavbar");
    if (!offcanvasElement) return;

    const offcanvasLinks = offcanvasElement.querySelectorAll(
      ".dropdown-item:not(.dropdown-toggle), .nav-link:not(.dropdown-toggle), .contactusbutton"
    );

    const handleLinkClick = () => {
      const bsOffcanvas = Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) bsOffcanvas.hide();
    };

    offcanvasLinks.forEach((link) =>
      link.addEventListener("click", handleLinkClick)
    );
    return () => {
      offcanvasLinks.forEach((link) =>
        link.removeEventListener("click", handleLinkClick)
      );
    };
  }, []);

  // Logo → home / scroll top
  const handleLogoClick = (e) => {
    e.preventDefault();
    closeOffcanvas();

    if (location.pathname === "/") {
      scrollToTop();
    } else {
      navigate("/", { replace: false });
    }
  };

  return (
    <nav
      className={
        navbar ? "navbar navbar-expand-lg active" : "navbar navbar-expand-lg"
      }
    >
      <div className="container">
        {/* Logo */}
        <NavLink
          className="navbar-brand"
          to="/"
          onClick={handleLogoClick}
          aria-label="NexTech PLM — Home"
        >
          <img
            className="nextech-logo"
            src={logo}
            alt="NexTech PLM Solutions"
          />
        </NavLink>

        {/* Toggle Button */}
        <button
          className="navbar-toggler shadow-none border-8"
          type="button"
          data-bs-toggle="offcanvas"
          data-bs-target="#offcanvasNavbar"
          aria-controls="offcanvasNavbar"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {/* Offcanvas Sidebar */}
        <div
          className="sidebar offcanvas offcanvas-start"
          tabIndex="-1"
          id="offcanvasNavbar"
          aria-labelledby="offcanvasNavbarLabel"
          data-bs-backdrop="false"
          data-bs-scroll="true"
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="offcanvas-title" id="offcanvasNavbarLabel">
              NexTech PLM
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white shadow-none"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            />
          </div>

          <div className="offcanvas-body navbar-font">
            <ul className="navbar-nav nav-pills justify-content-center flex-grow-1 pe-3 gap-2">
              {/* About */}
              <li className="nav-item mx-1">
                <NavLink
                  className={mainNavLinkClass}
                  to="/about"
                  onClick={scrollToTop}
                >
                  About Us
                </NavLink>
              </li>

              {/* PLM Implementation (dropdown trigger also pill) */}
              <li className="nav-item mx-1 dropdown position-static">
                <a
                  className="nav-link dropdown-toggle rounded-pill main-pill-link"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  PLM Implementation
                </a>
                <div className="dropdown-menu dropdown-menu-dark w-100 border-0 mt-0 p-3">
                  <div className="container">
                    <h1 className="pt-2 line">PLM Implementation</h1>
                    <div className="row">
                      <div className="col-lg-4 col-md-6">
                        <ul className="list-unstyled">
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-implementation"
                              onClick={scrollToTop}
                            >
                              Teamcenter Implementation
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-plm-document-management"
                              onClick={scrollToTop}
                            >
                              Document Management
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-mcad-integration"
                              onClick={scrollToTop}
                            >
                              MCAD Integration
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-ecad-integration"
                              onClick={scrollToTop}
                            >
                              ECAD Integration
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-business-process-management"
                              onClick={scrollToTop}
                            >
                              Business Processes &amp; Change Management
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-bom-management"
                              onClick={scrollToTop}
                            >
                              Product BOM and Variability Management
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-program-project-management"
                              onClick={scrollToTop}
                            >
                              Teamcenter Project Management
                            </NavLink>
                          </li>
                        </ul>
                      </div>

                      <div className="col-lg-4 col-md-6">
                        <ul className="list-unstyled">
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-performance-tuning-health-checkup"
                              onClick={scrollToTop}
                            >
                              Teamcenter Performance Tuning &amp; Health
                              Check-up
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-plm-erp-data-process-management"
                              onClick={scrollToTop}
                            >
                              PLM - ERP Data and Process Management
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-manufacturing-data-process-management"
                              onClick={scrollToTop}
                            >
                              Manufacturing Data and Process Management
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-cybersecurity"
                              onClick={scrollToTop}
                            >
                              Teamcenter Cyber Security
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-rac-to-aw-client-transition"
                              onClick={scrollToTop}
                            >
                              Teamcenter RAC to AW Client Transition
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-cloud-implementation"
                              onClick={scrollToTop}
                            >
                              Teamcenter Implementation on Cloud
                            </NavLink>
                          </li>
                        </ul>
                      </div>

                      <div className="col-lg-4 col-md-6">
                        <ul className="list-unstyled">
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-reporting-analytics"
                              onClick={scrollToTop}
                            >
                              Reporting and Analytics
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-aw-client"
                              onClick={scrollToTop}
                            >
                              Implementation of Teamcenter AW Client
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-cae-integration"
                              onClick={scrollToTop}
                            >
                              Teamcenter Integration for CAE
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-cam-integration"
                              onClick={scrollToTop}
                            >
                              Teamcenter Integration for CAM
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-legacy-pdm-management"
                              onClick={scrollToTop}
                            >
                              Legacy-PDM Management
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-upgrade-migration"
                              onClick={scrollToTop}
                            >
                              Teamcenter Upgrade &amp; Migration
                            </NavLink>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </li>

              {/* Solutions (dropdown trigger also pill) */}
              <li className="nav-item mx-1 dropdown position-static">
                <a
                  className="nav-link dropdown-toggle rounded-pill main-pill-link"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Solutions
                </a>
                <div className="dropdown-menu dropdown-menu-dark w-100 border-0 mt-0 p-3">
                  <div className="container">
                    <h1 className="pt-2 line">Solutions</h1>
                    <div className="row">
                      <div className="col-lg-6 col-md-6">
                        <ul className="list-unstyled">
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/mendix-apps-solutions"
                              onClick={scrollToTop}
                            >
                              Mendix Apps Solution
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/polarion-alm-solution"
                              onClick={scrollToTop}
                            >
                              Polarian ALM Solution
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-redefining-plm-solution"
                              onClick={scrollToTop}
                            >
                              Redefining PLM Solutions
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/sharepoint-insight-pdm-implementation"
                              onClick={scrollToTop}
                            >
                              SharePoint
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/Opcenter"
                              onClick={scrollToTop}
                            >
                              Opcenter Solutions
                            </NavLink>
                          </li>
                        </ul>
                      </div>

                      <div className="col-lg-6 col-md-6">
                        <ul className="list-unstyled">
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-software-development-services"
                              onClick={scrollToTop}
                            >
                              Software Development
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-support-consulting-services"
                              onClick={scrollToTop}
                            >
                              Support and Consulting
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-staff-augmentation-services"
                              onClick={scrollToTop}
                            >
                              Staff Augmentation
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="dropdown-item"
                              to="/teamcenter-plm-solutions-amc"
                              onClick={scrollToTop}
                            >
                              PLM Solutions AMC
                            </NavLink>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </li>

              {/* Product Sales */}
              <li className="nav-item mx-1">
                <NavLink
                  className={mainNavLinkClass}
                  to="/product-sales"
                  onClick={scrollToTop}
                >
                  Product Sales
                </NavLink>
              </li>

              {/* Careers */}
              <li className="nav-item mx-1">
                <NavLink
                  className={mainNavLinkClass}
                  to="/career"
                  onClick={scrollToTop}
                >
                  Careers
                </NavLink>
              </li>
            </ul>

            {/* Contact Us button on the right / bottom */}
            <div className="d-flex justify-content-center align-items-center gap-3 mt-2">
              <NavLink
                to="/contact"
                onClick={() => {
                  scrollToTop();
                  closeOffcanvas();
                }}
                className="text-decoration-none px-3 text-white py-1 contactusbutton rounded-4"
              >
                Contact Us
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

